import { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { GOAL_TITLE, parseNumericInput, type Goal, type User } from '@jungles/shared';
import { api } from '../lib/api';
import { useSession } from '../app/session';
import { Screen } from '../components/Chrome';
import { PencilIcon } from '../components/icons';

/** P1 · Знакомство (SPEC §5). От цели зависят готовые тренировки. */

import { GOAL_OPTIONS as GOALS } from '../lib/goal-options';

export function OnboardingScreen() {
  const { user, updateUser } = useSession();
  const navigate = useNavigate();

  const [height, setHeight] = useState(user?.heightCm ? String(user.heightCm) : '178');
  const [weight, setWeight] = useState(user?.weightKg ? String(user.weightKg) : '76.5');
  const [goal, setGoal] = useState<Goal>(user?.goal ?? 'maintain');
  const [error, setError] = useState<string | null>(null);
  const [busy, setBusy] = useState(false);

  const firstName = (user?.name ?? 'Игрок').split(' ')[0];

  const submit = async () => {
    const heightCm = parseNumericInput(height, true);
    const weightKg = parseNumericInput(weight);

    if (!Number.isInteger(heightCm) || heightCm < 100 || heightCm > 250) {
      setError('Рост — от 100 до 250 см');
      return;
    }
    if (!Number.isFinite(weightKg) || weightKg < 30 || weightKg > 300) {
      setError('Вес — от 30 до 300 кг');
      return;
    }

    setBusy(true);
    setError(null);
    try {
      const next = await api.post<User>('/api/me/onboarding', {
        heightCm,
        weightKg: Math.round(weightKg * 10) / 10,
        goal,
      });
      updateUser(next);
      navigate('/', { replace: true });
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Не удалось сохранить');
      setBusy(false);
    }
  };

  return (
    <Screen wide>
      <div className="top">
        <span className="brand">
          jungles<i> /</i>
        </span>
        <span className="prog" aria-label="Шаг 1 из 1">
          <i />
        </span>
      </div>

      <h1 className="hl3">
        {firstName}, расскажите
        <br />о себе
      </h1>

      <div className="nums">
        <label className="num">
          <small>Рост</small>
          <input
            type="text"
            inputMode="numeric"
            value={height}
            onChange={(event) => setHeight(event.target.value)}
            aria-label="Рост, см"
          />
          <span>см</span>
          <i className="pen" aria-hidden="true">
            <PencilIcon />
          </i>
        </label>
        <label className="num">
          <small>Текущий вес</small>
          <input
            type="text"
            inputMode="decimal"
            step="0.1"
            value={weight}
            onChange={(event) => setWeight(event.target.value)}
            aria-label="Вес, кг"
          />
          <span>кг</span>
          <i className="pen" aria-hidden="true">
            <PencilIcon />
          </i>
        </label>
      </div>

      <div style={{ fontSize: 15, fontWeight: 600, margin: '22px 2px 10px' }} id="goal-label">
        Ваша цель
      </div>
      <div className="plans" role="radiogroup" aria-labelledby="goal-label">
        {GOALS.map((item) => (
          <button
            key={item.id}
            type="button"
            role="radio"
            aria-checked={goal === item.id}
            className={goal === item.id ? 'plan on' : 'plan'}
            onClick={() => setGoal(item.id)}
          >
            <span>
              <b>{GOAL_TITLE[item.id]}</b>
              <small>{item.hint}</small>
            </span>
            <img src={item.icon} alt="" />
          </button>
        ))}
      </div>

      <p className="nt" style={{ margin: '12px 2px 0' }}>
        От цели зависят готовые тренировки. Поменять можно в профиле.
      </p>
      {error ? <p className="err">{error}</p> : null}

      <div className="foot tight">
        <button className="b p big" type="button" onClick={submit} disabled={busy}>
          {busy ? 'Сохраняем…' : 'Продолжить'}
        </button>
      </div>
    </Screen>
  );
}
